import React from "react";
import { USE_LOCAL_FALLBACK } from "../../config/firebaseConfig";
import { uniforms_getInventory, uniforms_recordSale } from "../../lib/firestoreAdapters";
import { useOutletContext } from "react-router-dom";

function invKey(campus) { return `uniforms_inventory_${String(campus || "gen").toLowerCase()}`; }
function loadInv(campus) { try { const raw = localStorage.getItem(invKey(campus)); return raw ? JSON.parse(raw) : []; } catch { return []; } }
function saveInv(campus, data) { try { localStorage.setItem(invKey(campus), JSON.stringify(data)); } catch { void 0; } }
function salesKey(campus) { return `uniforms_sales_${String(campus || "gen").toLowerCase()}`; }
function loadSales(campus) { try { const raw = localStorage.getItem(salesKey(campus)); return raw ? JSON.parse(raw) : []; } catch { return []; } }
function saveSales(campus, data) { try { localStorage.setItem(salesKey(campus), JSON.stringify(data)); } catch { void 0; } }

export default function Sales() {
  const { campus } = useOutletContext();
  const [inv, setInv] = React.useState(() => loadInv(campus));
  const [items, setItems] = React.useState([]);
  const [q, setQ] = React.useState("");
  const [saleDate, setSaleDate] = React.useState(() => new Date().toISOString().slice(0,10));
  React.useEffect(() => {
    async function fetch() {
      if (USE_LOCAL_FALLBACK) {
        setInv(loadInv(campus));
      } else {
        const data = await uniforms_getInventory(campus);
        setInv(data);
      }
      setItems([]);
    }
    fetch();
  }, [campus]);
  const catalog = React.useMemo(() => {
    const s = q.trim().toLowerCase();
    const base = inv;
    if (!s) return base;
    return base.filter(r => String(r.product).toLowerCase().includes(s) || String(r.size).toLowerCase().includes(s));
  }, [inv, q]);
  function addToCart(row) {
    const exist = items.find(i => i.product === row.product && i.size === row.size);
    if (exist) {
      setItems(items.map(i => i === exist ? { ...i, qty: i.qty + 1 } : i));
    } else {
      setItems([...items, { product: row.product, size: row.size, price: Number(row.price || 0), qty: 1 }]);
    }
  }
  function updateQty(idx, qty) {
    const n = Math.max(0, Number(qty || 0));
    setItems(items.map((it, i) => i === idx ? { ...it, qty: n } : it));
  }
  const total = items.reduce((s, it) => s + it.qty * Number(it.price || 0), 0);
  async function checkout() {
    if (!items.length) return;
    if (USE_LOCAL_FALLBACK) {
      const nextInv = inv.map(i => ({ ...i }));
      for (const it of items) {
        const idx = nextInv.findIndex(r => r.product === it.product && r.size === it.size);
        if (idx >= 0) nextInv[idx].stock = Number(nextInv[idx].stock || 0) - Number(it.qty || 0);
      }
      saveInv(campus, nextInv); setInv(nextInv);
      const sales = loadSales(campus);
      sales.push({
        id: `S-${Date.now()}`,
        campus, date: saleDate,
        items, total
      });
      saveSales(campus, sales);
      setItems([]);
      setSaleDate(new Date().toISOString().slice(0,10));
      alert("Sale recorded and inventory updated.");
    } else {
      await uniforms_recordSale(campus, items, { date: saleDate });
      const data = await uniforms_getInventory(campus);
      setInv(data);
      setItems([]);
      setSaleDate(new Date().toISOString().slice(0,10));
      alert("Sale recorded (Firestore) and inventory updated.");
    }
  }
  return (
    <div>
      {USE_LOCAL_FALLBACK && (
        <div className="alert alert-warning">
          Development mode: using local browser storage. In production this will use Firestore.
        </div>
      )}
      <div className="row g-3">
        <div className="col-md-7">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Catalog — {campus.toUpperCase()}</h5>
                <input className="form-control" placeholder="Search product/size" style={{ width: 260 }} value={q} onChange={e => setQ(e.target.value)} />
              </div>
              <div className="table-responsive" style={{ maxHeight: 360, overflowY: "auto" }}>
                <table className="table table-sm table-hover">
                  <thead><tr><th>Name</th><th>Size</th><th className="text-end">Price</th><th className="text-end">On Hand</th><th></th></tr></thead>
                  <tbody>
                    {catalog.length === 0 ? (
                      <tr><td colSpan="5" className="text-muted text-center">No items</td></tr>
                    ) : catalog.map((r, i) => (
                      <tr key={i}>
                        <td>{r.product}</td>
                        <td>{r.size}</td>
                        <td className="text-end">₱{Number(r.price || 0).toLocaleString()}</td>
                        <td className="text-end">{Number(r.stock || 0)}</td>
                        <td className="text-end">
                          <button className="btn btn-sm btn-outline-primary" onClick={() => addToCart(r)} disabled={Number(r.stock || 0) <= 0}>Add</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
        <div className="col-md-5">
          <div className="card">
            <div className="card-body">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h5 className="mb-0">Cart</h5>
                <div className="fw-bold">Total: ₱{total.toLocaleString()}</div>
              </div>
              <div className="mb-3">
                <label className="form-label fw-semibold">Sale Date</label>
                <input
                  type="date"
                  className="form-control"
                  value={saleDate}
                  onChange={e => setSaleDate(e.target.value)}
                />
                <div className="form-text">
                  Set this to a previous year or earlier month if you are encoding historical uniform sales.
                </div>
              </div>
              <div className="table-responsive">
                <table className="table table-sm">
                  <thead><tr><th>Item</th><th>Size</th><th className="text-end">Price</th><th style={{ width: 84 }}>Qty</th><th className="text-end">Subtotal</th><th></th></tr></thead>
                  <tbody>
                    {items.length === 0 ? (
                      <tr><td colSpan="6" className="text-center text-muted">No items in cart</td></tr>
                    ) : items.map((it, idx) => (
                      <tr key={idx}>
                        <td>{it.product}</td>
                        <td>{it.size}</td>
                        <td className="text-end">₱{Number(it.price || 0).toLocaleString()}</td>
                        <td><input type="number" className="form-control form-control-sm" value={it.qty} onChange={e => updateQty(idx, e.target.value)} /></td>
                        <td className="text-end">₱{(it.qty * Number(it.price || 0)).toLocaleString()}</td>
                        <td><button className="btn btn-sm btn-outline-danger" onClick={() => setItems(items.filter((_, i) => i !== idx))}>Remove</button></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="text-end">
                <button className="btn btn-success" onClick={checkout} disabled={items.length === 0}>Checkout</button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
